/**
 * AssessmentWrapper.js
 * A component that dynamically loads and renders the appropriate assessment components
 * based on the student's grade level (Foundational, Preparatory, or Middle stage)
 */
import React from 'react';
import {AlertCircle} from 'lucide-react';

// Import the correct stage-specific components
import CaregiverObservation from '../components/CaregiverObservation';
import PreparatoryCaregiverObservation from '../components/PreparatoryCaregiverObservation';
import MiddleStageCaregiverObservation from '../components/MiddleStageCaregiverObservation';

import StudentSelfAssessment from '../components/StudentSelfAssessment';
import PreparatorySelfAssessment from '../components/PreparatorySelfAssessment';
import MiddleStageSelfAssessmentViewer from '../components/MiddleStageSelfAssessmentViewer';

import PeerAssessment from '../components/PeerAssessment';
import PreparatoryPeerAssessment from '../components/PreparatoryPeerAssessment';
import MiddleStagePeerAssessmentViewer from '../components/MiddleStagePeerAssessmentViewer';

const AssessmentWrapper = ({
                               submission,
                               assignmentId,
                               childId,
                               isPreparatory,
                               isMiddleStage,
                               onCaregiverObservationComplete,
                               // Props for foundational stage
                               observationForm,
                               setObservationForm,
                               handleResourceChange,
                               handleCommentsChange,
                               handleObservationSubmit,
                               // Props for other stages
                               handleAssessmentChange,
                               handleSupportChange,
                               handleUnderstandingChange
                           }) => {
    console.log(`AssessmentWrapper - Rendering with:
        isPreparatory: ${isPreparatory}, 
        isMiddleStage: ${isMiddleStage}, 
        childId: ${childId}, 
        assignmentId: ${assignmentId},
        has submission: ${!!submission}`);

    // Handle nested submission structure from API
    const actualSubmission = submission?.submission || submission;

    if (submission) {
        console.log(`Submission data structure:`,
            Object.keys(submission).join(', '),
            actualSubmission?.caregiver_observation ? 'has caregiver_observation' : 'no caregiver_observation',
            actualSubmission?.self_assessment ? 'has self_assessment' : 'no self_assessment',
            actualSubmission?.peer_assessment ? 'has peer_assessment' : 'no peer_assessment'
        );
        
        if (actualSubmission && actualSubmission !== submission) {
            console.log('Using nested submission data structure');
        }
    }

    // Safe render function to prevent errors from breaking the entire UI
    const safeRender = (title, content) => {
        try {
            return content();
        } catch (error) {
            console.error(`Error rendering ${title}:`, error);
            return (
                <div
                    className="bg-white font-[poppins] shadow-sm rounded-lg overflow-hidden mb-6 border border-red-200">
                    <div className="px-4 py-3 bg-red-100">
                        <h2 className="text-lg font-medium text-center">{title} (Error)</h2>
                    </div>
                    <div className="p-6 text-center">
                        <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-3"/>
                        <p className="text-gray-800">There was an error displaying this component.</p>
                        <div className="mt-4 p-3 bg-gray-50 text-left rounded text-sm">
                            <strong>Error:</strong> {error.message || String(error)}
                        </div>
                    </div>
                </div>
            );
        }
    };

    // Render Caregiver Observation based on stage
    const renderCaregiverObservation = () => {
        if (isMiddleStage) {
            console.log("Rendering Middle Stage Caregiver Observation");
            return (
                <MiddleStageCaregiverObservation
                    childId={childId}
                    assignmentId={assignmentId}
                    submission={actualSubmission}
                    onSubmitComplete={onCaregiverObservationComplete}
                />
            );
        } else if (isPreparatory) {
            console.log("Rendering Preparatory Caregiver Observation");
            return (
                <PreparatoryCaregiverObservation
                    childId={childId}
                    assignmentId={assignmentId}
                    submission={actualSubmission}
                    onSubmitComplete={onCaregiverObservationComplete}
                />
            );
        } else {
            console.log("Rendering Foundational Caregiver Observation");
            // Foundational stage
            return (
                <CaregiverObservation
                    observationForm={observationForm}
                    handleResourceChange={handleResourceChange}
                    handleCommentsChange={handleCommentsChange}
                    handleObservationSubmit={handleObservationSubmit}
                    selectedSubmission={actualSubmission}
                    setObservationForm={setObservationForm}
                    isPreparatory={false}
                />
            );
        }
    };

    // Render Self Assessment based on stage
    const renderSelfAssessment = () => {
        if (!actualSubmission || !actualSubmission.self_assessment) {
            return (
                <div
                    className="bg-white font-[poppins] shadow-sm rounded-lg overflow-hidden mb-6 border border-gray-200">
                    <div className="px-4 py-3 bg-orange-100">
                        <h2 className="text-lg font-medium text-center uppercase">
                            Student's Self Reflection
                        </h2>
                    </div>
                    <div className="p-6 text-center">
                        <AlertCircle className="h-12 w-12 text-orange-200 mx-auto mb-3"/>
                        <p className="text-gray-500">No self-assessment has been submitted yet.</p>
                    </div>
                </div>
            );
        }

        if (isMiddleStage) {
            console.log("Rendering Middle Stage Self Assessment");
            return <MiddleStageSelfAssessmentViewer submission={actualSubmission}/>;
        } else if (isPreparatory) {
            console.log("Rendering Preparatory Self Assessment");
            return <PreparatorySelfAssessment submission={actualSubmission}/>;
        } else {
            console.log("Rendering Foundational Self Assessment");
            return <StudentSelfAssessment selfAssessment={actualSubmission.self_assessment}/>;
        }
    };

    // Render Peer Assessment based on stage
    const renderPeerAssessment = () => {
        if (!actualSubmission || !actualSubmission.peer_assessment) {
            return (
                <div
                    className="bg-white font-[poppins] shadow-sm rounded-lg overflow-hidden mb-6 border border-gray-200">
                    <div className="px-4 py-3 bg-orange-100">
                        <h2 className="text-lg font-medium text-center uppercase">
                            Peer Feedback
                        </h2>
                    </div>
                    <div className="p-6 text-center">
                        <AlertCircle className="h-12 w-12 text-orange-200 mx-auto mb-3"/>
                        <p className="text-gray-500">No peer assessment has been submitted yet.</p>
                    </div>
                </div>
            );
        }

        if (isMiddleStage) {
            console.log("Rendering Middle Stage Peer Assessment");
            return <MiddleStagePeerAssessmentViewer submission={actualSubmission}/>;
        } else if (isPreparatory) {
            console.log("Rendering Preparatory Peer Assessment");
            return <PreparatoryPeerAssessment submission={actualSubmission}/>;
        } else {
            console.log("Rendering Foundational Peer Assessment");
            return <PeerAssessment peerAssessment={actualSubmission.peer_assessment}/>;
        }
    };

    return (
        <div className="space-y-6">
            {safeRender("Parent-Teacher Partnership", renderCaregiverObservation)}
            {actualSubmission && safeRender("Student's Self Reflection", renderSelfAssessment)}
            {actualSubmission && safeRender("Peer Feedback", renderPeerAssessment)}
        </div>
    );
};

export default AssessmentWrapper;